import { ReactNode, useEffect, useState } from "react";

// MUI
import { Box, Skeleton, Stack, SvgIcon } from "@mui/material";

// CUSTOM COMPONENTS
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import QueueTitle from "./QueueTitle";

import QueuePosition from "./QueuePosition";

import QueueMappingOverflow from "./QueueMappingOverflow";

// TYPES
import { IManagedPosition, IPositionQueue, IQueue, IUserQueue, IUserWithQueue } from "@/Settings/types/QueueMapping/QueueMapping";

// CONSTANTS
import { DEFAULT_QUEUE_NAME } from "@/Settings/Constants/QueueMapping";

// ICONS
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";
import QueueMappingSelectors from "./QueueMappingSelectors";

const JUSTIFY_CONTENT_SPACE_BETWEEN = "space-between";
interface IQueueMappingDetailsProps {
	queueLabels: string[];
	isQueueLabelsLoading: boolean;
	handleQueueName: (title: string) => void;
	handleUpdateOverflowQueues: (overflowQueues: string[]) => void;
	queues: IQueue[];
	managedPositions: string[];
	selectedPositions: IManagedPosition[];
	onPositionsChange: (selected: IManagedPosition[]) => void;
	queue: IQueue;
	associates: IUserWithQueue[];
	employees: IUserWithQueue[];
	isPositionsLoading: boolean;
	isEmployeesLoading: boolean;
	isUsersLoading: boolean;
	selectedUsers: IPositionQueue[];
	onAssociatesChange: (selected: IUserWithQueue[]) => void;
	onEmployeesChange: (selected: IUserWithQueue[]) => void;
	onDeletePosition: (positionId: string) => void;
	onDeleteMembers: (usersToRemove: IPositionQueue[]) => void;
	handleSelectUser: (position: string, user: IUserQueue) => void;
	uniqueUsers: string[] | ReactNode;
}

const QueueMappingDetails = (props: IQueueMappingDetailsProps) => {
	const {
		queueLabels,
		isQueueLabelsLoading,
		queues,
		handleQueueName,
		handleUpdateOverflowQueues,
		queue,
		isPositionsLoading,
		isEmployeesLoading,
		selectedUsers,
		onDeletePosition,
		onDeleteMembers,
		handleSelectUser,
		managedPositions,
		selectedPositions,
		onPositionsChange,
		associates,
		employees,
		isUsersLoading,
		onAssociatesChange,
		onEmployeesChange,
		uniqueUsers,
	} = props;

	const [isOverflow, setIsOverflow] = useState(queue?.overflowQueues?.length > 0);
	const [nameError, setNameError] = useState({ name: DEFAULT_QUEUE_NAME, error: true });
	const isLoading = isPositionsLoading || isEmployeesLoading;

	const handleIsOverflow = (value: boolean) => {
		if (!value) {
			handleUpdateOverflowQueues([]);
		}
		setIsOverflow(value);
	};

	useEffect(() => {
		setIsOverflow(queue?.overflowQueues?.length > 0);
	}, [queue]);

	return (
		<>
			<Stack display={"flex"} direction={"row"} justifyContent={"flex-start"} gap={2}>
				<QueueTitle
					queueLabels={queueLabels}
					isQueueLabelsLoading={isQueueLabelsLoading}
					queue={queue}
					handleQueueName={handleQueueName}
					queues={queues}
					handleNameError={setNameError}
				/>
				<Stack mb={0.5}>
					<CustomizedSwitches
						key={"overflowQueueSwitch"}
						textChecked={"Overflow"}
						textUnChecked={"Overflow"}
						textRight={true}
						checked={isOverflow}
						onChange={(value) => handleIsOverflow(value)}
						disabled={nameError.error}
					/>
				</Stack>
			</Stack>
			<QueueMappingOverflow
				queues={queues}
				mainQueue={queue}
				handleUpdateOverflowQueues={handleUpdateOverflowQueues}
				isOverflowChecked={isOverflow}
				queueLabels={queueLabels}
			/>
			<Stack flex={"flex"} direction={"column"} spacing={2} ml={1} mt={3}>
				{isLoading ? (
					<Stack display={"flex"} direction={"row"} justifyContent={JUSTIFY_CONTENT_SPACE_BETWEEN} gap={2}>
						<Stack direction={"column"} gap={2}>
							<Skeleton variant="rectangular" width={160} height={27} />
							<Skeleton variant="rounded" width={253} height={32} />
						</Stack>
						<Stack direction={"column"} gap={2}>
							<Skeleton variant="rectangular" width={160} height={27} />
							<Skeleton variant="rounded" width={253} height={32} />
							<Box gap={1} mt={-1.5}>
								<Skeleton variant="rectangular" width={138} height={18} />
								<Skeleton variant="rectangular" width={350} height={18} className="mt-1" />
							</Box>
						</Stack>
						<Stack direction={"column"} gap={2} mr={8}>
							<Skeleton variant="rectangular" width={160} height={27} />
							<Skeleton variant="rounded" width={253} height={32} />
						</Stack>
					</Stack>
				) : (
					<QueueMappingSelectors
						managedPositions={managedPositions}
						selectedPositions={selectedPositions}
						onPositionsChange={onPositionsChange}
						queue={queue}
						associates={associates}
						employees={employees}
						isPositionsLoading={isPositionsLoading}
						isEmployeesLoading={isEmployeesLoading}
						isUsersLoading={isUsersLoading}
						onAssociatesChange={onAssociatesChange}
						onEmployeesChange={onEmployeesChange}
						uniqueUsers={uniqueUsers}
						nameError={nameError}
					/>
				)}
			</Stack>
			<Stack display={"flex"} direction={"column"} mt={3} gap={4}>
				{selectedUsers.length > 0 && (
					<Box display={"flex"} alignItems={"center"} justifyContent={"flex-end"} position={"absolute"} right={68}>
						<DoubleCheckingButton
							icon={
								<SvgIcon
									className="!text-[16px] ml-[1px] [&>path]:fill-gray-blue"
									component={TrashIcon}
									viewBox={"0 0 14 14"}
								/>
							}
							title={"Remove users"}
							endIcon={<SvgIcon className="!text-[16px] ml-[4px]" component={TrashIcon} viewBox={"0 0 14 14"} />}
							confirmationButtonClasses={"btn-table-confirmation-remove"}
							mainButtonClasses={"btn-simple-remove btn-pack-remove"}
							disabled={false}
							mainSx={{ marginLeft: "90px" }}
							confirmationSx={{ right: 0, left: "10px", width: "128px !important" }}
							onDeleteAction={() => onDeleteMembers(selectedUsers)}
						/>
					</Box>
				)}
				{queue?.usersByPosition?.length > 0 &&
					queue.usersByPosition.map((pos) => (
						<Box key={pos._id}>
							<QueuePosition
								position={pos}
								selectedUsers={selectedUsers}
								onDeleteAllMembers={onDeletePosition}
								handleSelectUser={handleSelectUser}
							/>
						</Box>
					))}
			</Stack>
		</>
	);
};

export default QueueMappingDetails;
